import * as React from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { toast } from "sonner";

import { Spinner } from "@/components/Spinner";
import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import {
  OrgsEmptyState,
  OrgsListSkeleton,
} from "@/features/wonda/components/OrganizationsList";
import { OrgListRow } from "@/features/wonda/components/OrgListRow";
import { CliFooter } from "@/landing/components/CliFooter";
import { CliNav } from "@/landing/components/CliNav";
import { Route as rootRoute } from "@/routes/__root";

export const Route = createFileRoute(
  "/_authenticated/(wonda)/_wonda/organizations/",
)({
  component: OrgsListPage,
});

// Shell for the org-list page. The light
// ground comes from the global html/body base paint.
function OrgsShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-[100dvh] flex-col">
      <CliNav />
      {children}
      <CliFooter />
    </div>
  );
}

function OrgsListPage() {
  const navigate = useNavigate();
  const api = rootRoute.useRouteContext({ select: (ctx) => ctx.api });
  const apiUtils = api.useUtils();
  const [isCreateOpen, setIsCreateOpen] = React.useState(false);
  const [pendingSlug, setPendingSlug] = React.useState<string | null>(null);

  const { data: orgs, isLoading } = api.organization.list.useQuery(undefined, {
    // While waiting for a freshly created org to land, poll the list so
    // we can navigate to the new org as soon as it appears.
    refetchInterval: pendingSlug ? 600 : false,
  });

  React.useEffect(() => {
    if (!pendingSlug || !orgs) return;
    const matched = orgs.find((row) => row.slug === pendingSlug);
    if (matched) {
      setPendingSlug(null);
      setIsCreateOpen(false);
      void navigate({
        to: "/organizations/$slug",
        params: { slug: matched.slug },
      });
    }
  }, [pendingSlug, orgs, navigate]);

  return (
    <OrgsShell>
      <main className="mx-auto w-full max-w-3xl px-4 py-12 sm:px-6">
        <div className="mb-8 flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold">Organizations</h1>
            <p className="mt-1 text-sm text-(--cli-text-muted)">
              Pool credits with a team. Invite members, assign seats, share one
              bill.
            </p>
          </div>
          <Dialog
            open={isCreateOpen}
            onOpenChange={(open) => {
              // Block close while we're waiting for the webhook to mirror.
              if (!open && pendingSlug) return;
              setIsCreateOpen(open);
            }}
          >
            <DialogTrigger asChild>
              <Button variant="steward" size="default">
                <Plus className="size-4" strokeWidth={1.5} />
                Create
              </Button>
            </DialogTrigger>
            <CreateOrgDialogContent
              isWaitingForWebhook={pendingSlug !== null}
              onCreated={(slug) => {
                setPendingSlug(slug);
                void apiUtils.organization.list.invalidate();
              }}
            />
          </Dialog>
        </div>

        {isLoading ? (
          <OrgsListSkeleton />
        ) : !orgs || orgs.length === 0 ? (
          <OrgsEmptyState description="Create one to start pooling credits with your team." />
        ) : (
          <ul className="flex flex-col gap-2">
            {orgs.map((org) => (
              <li key={org.organizationId}>
                <OrgListRow
                  name={org.name}
                  slug={org.slug}
                  seatPlanCode={org.seatPlanCode}
                  clickable={org.isOwner || org.clerkRole === "admin"}
                />
              </li>
            ))}
          </ul>
        )}
      </main>
    </OrgsShell>
  );
}

/**
 * Derive a URL-safe slug from the org name. Lowercase, spaces to
 * dashes, drop anything that isn't [a-z0-9-], collapse repeated dashes,
 * trim leading/trailing dashes. Mirrors the server-side regex
 * (^[a-z0-9-]+$) so the user never sees a slug-format error.
 */
function slugify(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function CreateOrgDialogContent({
  isWaitingForWebhook,
  onCreated,
}: {
  isWaitingForWebhook: boolean;
  onCreated: (slug: string) => void;
}) {
  const api = rootRoute.useRouteContext({ select: (ctx) => ctx.api });
  const [name, setName] = React.useState("");
  const slug = slugify(name);

  const create = api.organization.create.useMutation({
    onSuccess: () => {
      const createdSlug = slug;
      setName("");
      onCreated(createdSlug);
    },
    onError: (error) => toast.error(error.message),
  });

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName || !slug) return;
    create.mutate({ name: trimmedName, slug });
  };

  if (isWaitingForWebhook) {
    return (
      <DialogContent
        onEscapeKeyDown={(event) => event.preventDefault()}
        onPointerDownOutside={(event) => event.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Setting up your organization…</DialogTitle>
          <DialogDescription>
            Provisioning identity and billing. We'll take you to the org page in
            a moment.
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center justify-center py-6">
          <Spinner variant="wonda" />
        </div>
      </DialogContent>
    );
  }

  return (
    <DialogContent>
      <form onSubmit={submit} className="flex flex-col gap-5">
        <DialogHeader>
          <DialogTitle>Create organization</DialogTitle>
          <DialogDescription>
            You will become the first admin. You can buy seats and invite
            members from the org page.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <Label htmlFor="org-name">Name</Label>
          <Input
            id="org-name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Acme Inc"
            autoFocus
            maxLength={120}
            required
          />
          {slug ? (
            <p className="text-xs text-(--cli-text-muted)">
              URL slug:{" "}
              <code className="rounded bg-(--hair) px-1.5 py-0.5">{slug}</code>
            </p>
          ) : null}
        </div>
        <DialogFooter>
          <Button
            type="submit"
            variant="steward"
            disabled={!slug || create.isPending}
          >
            {create.isPending ? "Creating..." : "Create organization"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
